import { Component, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { HeaderComponent } from '../../components/header/header.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { CommonModule, DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { DatasearchService } from '../../_services/datasearch.service';

@Component({
  selector: 'app-our-story',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, NgFor, NgClass, NgIf, CommonModule],
  templateUrl: './our-story.component.html',
  styleUrls: ['./our-story.component.scss'],
  providers: [DatePipe]
})
export class OurStoryComponent implements AfterViewInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  logoBaseUrl = '';
  yearData: any[] = [];
  showLeftButton: boolean = false;
  showRightButton: boolean = false;
  head: any[] = [];
  currentPage: string = 'story';
  currentUrl: string;

  constructor(private datasearch: DatasearchService, private datePipe: DatePipe) {
    this.logoBaseUrl = this.datasearch.ApiUrl + '/uploads/';
    this.currentUrl = window.location.href; // Get the current URL

    this.getData();
  }

  ngOnInit() {
    this.getData();
    this.getHeading();
  }

  ngAfterViewInit() {
    this.updateButtonVisibility();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateButtonVisibility();
  }

  getData() {
    this.datasearch.getOurStory().subscribe(res => {
      this.yearData = this.groupStoriesByYear(res.data);
      this.updateButtonVisibility();
    });
  }

  groupStoriesByYear(stories: {
    _id: string;
    date: string;
    poster: string;
    title: string;
    description: string;
    images: string[];
    web_link?: string; // Include web_link as an optional property
  }[]): { year: number; web_link?: string; months: { month: number; stories: any[]; }[] }[] {
    const yearMap: { [key: number]: { year: number; web_link?: string; months: { month: number; stories: any[]; }[] } } = {};

    stories.forEach((story) => {
      const storyDate = new Date(story.date);
      const year = storyDate.getFullYear();
      const month = storyDate.getMonth(); // Get month (0-11)

      if (!yearMap[year]) {
        yearMap[year] = {
          year,
          web_link: story.web_link, // Store the web_link for the year
          months: Array(12).fill(null).map((_, index) => ({ month: index, stories: [] }))
        };
      }

      // Push the story to the appropriate month
      yearMap[year].months[month].stories.push({
        _id: story._id,
        title: story.title,
        description: story.description,
        date: story.date,
        poster: story.poster,
        images: story.images,
      });
    });

    // Sort the years
    return Object.values(yearMap)
      .sort((a, b) => b.year - a.year);
  }

  getStoryImageUrl(story: any): string {
    if (!story) return '';

    // Check for poster first as requested
    if (story.poster) {
      if (story.poster.toString().startsWith('http')) return story.poster;
      return this.datasearch.ApiUrl + '/uploads/story-posters/' + story.poster;
    }

    const filename = story.logo || story.image;
    if (!filename) return '';
    if (filename.toString().startsWith('http')) return filename;

    // Default to story-images for other image types
    return this.datasearch.ApiUrl + '/uploads/story-images/' + filename;
  }

  updateButtonVisibility() {
    if (!this.scrollContainer) return;

    const container = this.scrollContainer.nativeElement;
    const itemWidth = container.querySelector('.story_bottom_box_mid_container_content')?.offsetWidth || 300;
    const containerWidth = container.offsetWidth;

    const itemsPerView = Math.floor(containerWidth / itemWidth);
    const totalItems = this.yearData.length;

    this.showLeftButton = totalItems > itemsPerView;
    this.showRightButton = totalItems > itemsPerView;
  }

  scrollLeft() {
    const container = this.scrollContainer.nativeElement;
    const itemWidth = container.querySelector('.story_bottom_box_mid_container_content')?.offsetWidth || 300;
    container.scrollBy({
      left: -itemWidth,
      behavior: 'smooth'
    });
  }

  scrollRight() {
    const container = this.scrollContainer.nativeElement;
    const itemWidth = container.querySelector('.story_bottom_box_mid_container_content')?.offsetWidth || 300;
    container.scrollBy({
      left: itemWidth,
      behavior: 'smooth'
    });
  }

  getHeading() {
    this.datasearch.getAllHeadings().subscribe(res => {
      if (res.success) {
        this.head = res.data.filter((heading: any) => heading.page === this.currentPage);
      }
    });
  }
  openLink(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }
  shareViaEmail() {
    const subject = 'Check out this page!';
    const body = `Here is the link: ${this.currentUrl}`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink; // Open the email client
  }

  shareViaSMS() {
    const smsLink = `sms:?body=${encodeURIComponent(this.currentUrl)}`;
    window.location.href = smsLink; // Open SMS app
  }

  shareViaWhatsApp() {
    const text = `Check this out! ${this.currentUrl}`;
    const whatsappLink = `https://wa.me/?text=${encodeURIComponent(text)}`;

    window.open(whatsappLink, '_blank'); // Open WhatsApp share dialog
  }

  shareViaMessenger() {
    const messengerLink = `https://www.messenger.com/share/?link=${encodeURIComponent(this.currentUrl)}`;
    window.open(messengerLink, '_blank'); // Open Messenger share dialog
  }

  shareViaViber() {
    const viberLink = `viber://forward?text=${encodeURIComponent(this.currentUrl)}`;
    window.open(viberLink, '_blank'); // Open Viber share dialog
  }

  shareViaFacebook() {
    const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.currentUrl)}`;

    window.open(facebookLink, '_blank'); // Open Facebook share dialog
  }

  shareViaTwitter() {
    const text = `Check this out! ${this.currentUrl}`;
    const twitterLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;

    window.open(twitterLink, '_blank'); // Open Twitter share dialog
  }

  shareViaLinkedIn() {
    const linkedInLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(linkedInLink, '_blank'); // Open LinkedIn share dialog
  }

  shareViaPinterest() {
    const pinterestLink = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(pinterestLink, '_blank'); // Open Pinterest share dialog
  }

  shareViaReddit() {
    const redditLink = `https://www.reddit.com/submit?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(redditLink, '_blank'); // Open Reddit share dialog
  }

  shareViaTumblr() {
    const tumblrLink = `https://www.tumblr.com/share/link?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(tumblrLink, '_blank'); // Open Tumblr share dialog
  }

  shareViaTelegram() {
    const telegramLink = `https://t.me/share/url?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(telegramLink, '_blank'); // Open Telegram share dialog
  }

  shareViaVK() {
    const vkLink = `https://vk.com/share.php?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(vkLink, '_blank'); // Open VK share dialog
  }

  shareViaMix() {
    const mixLink = `https://mix.com/add?url=${encodeURIComponent(this.currentUrl)}`;
    window.open(mixLink, '_blank'); // Open Mix share dialog
  }

  shareViaXing() {
    const xingLink = `https://www.xing.com/app/user?op=share;url=${encodeURIComponent(this.currentUrl)}`;
    window.open(xingLink, '_blank'); // Open Xing share dialog
  }

  shareViaLine() {
    const lineLink = `https://line.me/R/share?text=${encodeURIComponent(this.currentUrl)}`;
    window.open(lineLink, '_blank'); // Open Line share dialog
  }

  copyLink() {
    navigator.clipboard.writeText(this.currentUrl).then(() => {
      alert('Link copied to clipboard!');
    });
  }
}
